import axios from "axios";
import { PrismicDocumentViewedApiModel } from "../../api-models";
import { CmsDocumentTypeMap, CmsDocumentTypes } from "./cms-type-configuration";
import {
  CmsLeaderType,
  CmsPersonalPathwayStep,
  CmsTeamPathwayStep,
  CmsResource,
  CmsResourcePlaylist,
  CmsRichText,
  CmsSessionWithSlides,
  CmsStrengthItem,
  CmsWorkshopPage,
  CmsPersonalPathway,
  CmsTeamPathway,
  CmsGoalCapturePage,
  CmsTeamLevelsAndMilestones,
  CmsPersonalLevelsAndMilestones,
} from "./cms-type-definitions";
import { CmsReport } from "./types/cms-report";
import { csContext } from "../cs-context";

async function trackEvent(uid: string, type: string, name: string) {
  if (!csContext.user) {
    return;
  }

  const model: PrismicDocumentViewedApiModel = { uid, type, name };
  await axios.post("/api/track/PrismicDocumentViewed", model);
}

// eslint-disable-next-line import/prefer-default-export
export const trackingHooks: {
  [x in CmsDocumentTypes]:
    | ((
        doc: CmsDocumentTypeMap[x],
        richTextRenderer: (richText: CmsRichText) => string,
      ) => Promise<void>)
    | null;
} = {
  "cms-develop-page": null,
  "cms-develop-plan": null,
  "cms-strength-item": async (
    doc: CmsStrengthItem,
    richTextRenderer: (richText: CmsRichText) => string,
  ) => {
    await trackEvent(doc.uid || "", doc.type, doc.data.name || richTextRenderer(doc.data.title));
  },
  "cms-carousel": null,
  "cms-variable": null,
  "cms-smart-search-prompts": null,
  "cms-info-page": null,
  "cms-quote": null,
  "cms-resource-themes-page": null,
  "cms-resource-theme": null,
  "cms-resource-playlist": async (
    doc: CmsResourcePlaylist,
    richTextRenderer: (richText: CmsRichText) => string,
  ) => {
    await trackEvent(doc.uid || "", doc.type, doc.data.name || richTextRenderer(doc.data.title));
  },
  "cms-resource": async (doc: CmsResource, richTextRenderer: (richText: CmsRichText) => string) => {
    await trackEvent(doc.uid || "", doc.type, doc.data.name || richTextRenderer(doc.data.title));
  },
  "cms-leader-type": async (
    doc: CmsLeaderType,
    richTextRenderer: (richText: CmsRichText) => string,
  ) => {
    await trackEvent(doc.uid || "", doc.type, doc.data.name || richTextRenderer(doc.data.title));
  },
  "cms-session": null,
  "cms-session-with-slides": async (
    doc: CmsSessionWithSlides,
    richTextRenderer: (richText: CmsRichText) => string,
  ) => {
    await trackEvent(doc.uid || "", doc.type, doc.data.name || richTextRenderer(doc.data.title));
  },
  "cms-link-resource": null,
  "cms-custom-strength-item": null,
  "cms-custom-strength-mapping": null,
  "cms-team-image-list": null,
  "cms-workshop-page": async (
    doc: CmsWorkshopPage,
    richTextRenderer: (richText: CmsRichText) => string,
  ) => {
    await trackEvent(doc.uid || "", doc.type, doc.data.name || richTextRenderer(doc.data.title));
  },
  "cms-session-wheel": null,
  "cms-team-effectiveness-review": null,
  "cms-report": async (doc: CmsReport, richTextRenderer: (richText: CmsRichText) => string) => {
    await trackEvent(doc.uid || "", doc.type, doc.data.name || richTextRenderer(doc.data.title));
  },
  "cms-package": null,
  "cms-digital-packages": null,
  "cms-subscription-policy": null,
  "cms-goal": null,
  "cms-pathway": async (
    doc: CmsTeamPathway,
    richTextRenderer: (richText: CmsRichText) => string,
  ) => {
    await trackEvent(doc.uid || "", doc.type, richTextRenderer(doc.data.title));
  },
  "cms-pathway-step": async (
    doc: CmsTeamPathwayStep,
    richTextRenderer: (richText: CmsRichText) => string,
  ) => {
    await trackEvent(doc.uid || "", doc.type, doc.data.name || richTextRenderer(doc.data.title));
  },
  "cms-personal-goal": null,
  "cms-personal-pathway": async (
    doc: CmsPersonalPathway,
    richTextRenderer: (richText: CmsRichText) => string,
  ) => {
    await trackEvent(doc.uid || "", doc.type, richTextRenderer(doc.data.title));
  },
  "cms-personal-pathway-step": async (
    doc: CmsPersonalPathwayStep,
    richTextRenderer: (richText: CmsRichText) => string,
  ) => {
    await trackEvent(doc.uid || "", doc.type, doc.data.name || richTextRenderer(doc.data.title));
  },
  "cms-smart-callout": null,
  "cms-smart-text-box": null,
  "cms-smart-journal-page": null,
  "cms-report-page": null,
  "cms-team-goal-capture-page": async (
    doc: CmsGoalCapturePage,
    richTextRenderer: (richText: CmsRichText) => string,
  ) => {
    await trackEvent(doc.uid || "", doc.type, richTextRenderer(doc.data.title));
  },
  "cms-team-level": null,
  "cms-team-milestone": null,
  "cms-team-levels": async (
    doc: CmsTeamLevelsAndMilestones,
    richTextRenderer: (richText: CmsRichText) => string,
  ) => {
    await trackEvent(doc.uid || "", doc.type, richTextRenderer(doc.data.title));
  },
  "cms-personal-level": null,
  "cms-personal-milestone": null,
  "cms-personal-levels": async (
    doc: CmsPersonalLevelsAndMilestones,
    richTextRenderer: (richText: CmsRichText) => string,
  ) => {
    await trackEvent(doc.uid || "", doc.type, richTextRenderer(doc.data.title));
  },
};
